import { LayoutProps, Menu, MenuProps, useSidebarState } from "react-admin";
import { Layout } from "react-admin";
import { AuAppBar } from "./AuAppBar";
import { Box, Button, Collapse, IconButton, Tooltip } from "@mui/material";
import { OrgPicker } from "./org-picker";
import { useContext, useState } from "react";
import { UserContext } from "../../logic/auth/user-context";
import { AuScope, hasScope } from "@arrowsup/platform-dtos";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import SettingsIcon from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PaletteIcon from "@mui/icons-material/Palette";
import CampaignIcon from "@mui/icons-material/Campaign";
import { QuivrColorLogo } from "./QuivrColorLogo";
import { JobQueuesMenuItem } from "./job-queues-menu-item";

export const AuLayout = (props: JSX.IntrinsicAttributes & LayoutProps) => (
  <Layout {...props} menu={AuMenu} appBar={AuAppBar} />
);

/**
 * The menu for the entire ReactAdmin frontend.
 * Was necessary to be able to hide/show different menu items, while keeping
 * the actual components themselves.
 */
export const AuMenu: React.FC<MenuProps> = () => {
  const userContext = useContext(UserContext);
  const permissions = userContext.user.policies;
  const canViewUsers = hasScope(permissions, AuScope.UsersRead);
  const canViewConfig = hasScope(permissions, AuScope.OrgConfigRead);
  const canViewDashboards = hasScope(permissions, AuScope.DashboardRead);
  const [sidebarOpen] = useSidebarState();
  const canViewConversions = hasScope(permissions, AuScope.ConversionRead);
  const canViewConversionFiltersList = hasScope(
    permissions,
    AuScope.ConversionFilterListRead
  );
  const canViewConversionOutputStream = hasScope(
    permissions,
    AuScope.ConversionOutputStreamRead
  );
  const canViewConversionInputStream = hasScope(
    permissions,
    AuScope.ConversionInputStreamRead
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showConversions, setShowConversions] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const [showFunnels, setShowFunnels] = useState(false);
  const [showMisc, setShowMisc] = useState(false);
  const [showAdvertising, setShowAdvertising] = useState(false);
  const canViewTracking = hasScope(permissions, AuScope.TrackingRead);
  const canViewFunnels = hasScope(permissions, AuScope.FunnelRead);
  const canViewRetainers = hasScope(permissions, AuScope.RetainerRead);
  const canViewJobQueues = hasScope(permissions, AuScope.JobQueuesAdmin);
  const canViewBrandKit = hasScope(permissions, AuScope.BrandKitRead);
  const canViewAdAccounts = hasScope(permissions, AuScope.AdAccountRead);
  const canViewAdDataStreams = hasScope(permissions, AuScope.AdDataStreamRead);
  const canViewConnections = hasScope(
    permissions,
    AuScope.IntegrationConnectionRead
  );
  const canViewAdvertising = canViewAdAccounts || canViewAdDataStreams;
  const canViewMisc =
    canViewUsers || canViewRetainers || canViewConfig || canViewJobQueues;
  const canViewConversionsGroup =
    canViewConversions ||
    canViewConversionFiltersList ||
    canViewConversionOutputStream ||
    canViewConversionInputStream ||
    canViewConnections;
  const canViewSettings =
    canViewMisc ||
    canViewConversionsGroup ||
    canViewTracking ||
    canViewFunnels ||
    canViewAdvertising;

  const menuItems: JSX.Element[] = [];

  // Anyone who belongs to more than one org can switch between them.
  if (sidebarOpen && (userContext.user.organizations?.length ?? 0) > 1) {
    menuItems.push(
      <Box key="org-picker" ml={2} mt={2} mb={2} mr={1}>
        <OrgPicker />
      </Box>
    );
  }

  if (canViewDashboards) {
    menuItems.push(<Menu.ResourceItem key="dashboard" name="dashboard" />);
  }

  if (canViewBrandKit) {
    menuItems.push(
      <Menu.Item
        key="brand-kit"
        to="/brand-kit"
        primaryText="Brand Kit"
        leftIcon={<PaletteIcon />}
      />
    );
  }

  if (canViewSettings) {
    menuItems.push(
      <Box key="advanced-group" sx={{ pl: 1 }}>
        {sidebarOpen ? (
          <Button
            color="inherit"
            onClick={() => setShowAdvanced((last) => !last)}
          >
            Settings
            {showAdvanced ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </Button>
        ) : (
          <Tooltip title="Settings" placement="right">
            <IconButton
              color="inherit"
              onClick={() => setShowAdvanced((last) => !last)}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}
        <Collapse in={showAdvanced}>
          {canViewMisc && (
            <Box key="misc-group" sx={{ pl: sidebarOpen ? 2 : 0 }}>
              {sidebarOpen ? (
                <Button
                  color="inherit"
                  onClick={() => setShowMisc((last) => !last)}
                >
                  Miscellaneous
                  {showMisc ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </Button>
              ) : (
                <Tooltip title="Miscellaneous" placement="right">
                  <IconButton
                    color="inherit"
                    onClick={() => setShowMisc((last) => !last)}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Collapse in={showMisc}>
                {canViewUsers && <Menu.ResourceItem key="users" name="users" />}
                {canViewRetainers && (
                  <Menu.ResourceItem key="retainer" name="retainer" />
                )}
                {canViewConfig && (
                  <Menu.Item
                    key="config"
                    to="/config"
                    primaryText="Account"
                    leftIcon={<BusinessIcon />}
                  />
                )}
                {canViewJobQueues && <JobQueuesMenuItem key="job-queues" />}
              </Collapse>
            </Box>
          )}

          {canViewConversionsGroup && (
            <Box key="conversions-group" sx={{ pl: sidebarOpen ? 2 : 0 }}>
              {sidebarOpen ? (
                <Button
                  color="inherit"
                  onClick={() => setShowConversions((last) => !last)}
                >
                  Conversions
                  {showConversions ? (
                    <ArrowDropUpIcon />
                  ) : (
                    <ArrowDropDownIcon />
                  )}
                </Button>
              ) : (
                <Tooltip title="Conversions" placement="right">
                  <IconButton
                    color="inherit"
                    onClick={() => setShowConversions((last) => !last)}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Collapse in={showConversions}>
                {canViewConversions && (
                  <Menu.ResourceItem key="conversion" name="conversion" />
                )}
                {canViewConversionFiltersList && (
                  <Menu.ResourceItem
                    key="conversion-filter"
                    name="conversion-filter"
                  />
                )}
                {canViewConversionOutputStream && (
                  <Menu.ResourceItem
                    key="conversion-output-stream"
                    name="conversion-output-stream"
                  />
                )}
                {canViewConversionInputStream && (
                  <Menu.ResourceItem
                    key="conversion-input-stream"
                    name="conversion-input-stream"
                  />
                )}
                {canViewConnections && (
                  <Menu.ResourceItem
                    key="integration-connection"
                    name="integration-connection"
                  />
                )}
              </Collapse>
            </Box>
          )}

          {canViewTracking && (
            <Box key="tracking-group" sx={{ pl: sidebarOpen ? 2 : 0 }}>
              {sidebarOpen ? (
                <Button
                  color="inherit"
                  onClick={() => setShowTracking((last) => !last)}
                >
                  Tracking
                  {showTracking ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </Button>
              ) : (
                <Tooltip title="Tracking" placement="right">
                  <IconButton
                    color="inherit"
                    onClick={() => setShowTracking((last) => !last)}
                  >
                    <TrackChangesIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Collapse in={showTracking}>
                <Menu.ResourceItem key="paid-source" name="paid-source" />
                <Menu.ResourceItem key="tracked-call" name="tracked-call" />
                <Menu.ResourceItem key="tracked-url" name="tracked-url" />
                <Menu.ResourceItem
                  key="custom-tracking-param"
                  name="custom-tracking-param"
                />
              </Collapse>
            </Box>
          )}

          {canViewAdvertising && (
            <Box key="advertising-group" sx={{ pl: sidebarOpen ? 2 : 0 }}>
              {sidebarOpen ? (
                <Button
                  color="inherit"
                  onClick={() => setShowAdvertising((last) => !last)}
                >
                  Advertising
                  {showAdvertising ? (
                    <ArrowDropUpIcon />
                  ) : (
                    <ArrowDropDownIcon />
                  )}
                </Button>
              ) : (
                <Tooltip title="Advertising" placement="right">
                  <IconButton
                    color="inherit"
                    onClick={() => setShowAdvertising((last) => !last)}
                  >
                    <CampaignIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Collapse in={showAdvertising}>
                {canViewAdAccounts && (
                  <Menu.ResourceItem key="ad-account" name="ad-account" />
                )}
                {canViewAdDataStreams && (
                  <Menu.ResourceItem
                    key="ad-data-stream"
                    name="ad-data-stream"
                  />
                )}
              </Collapse>
            </Box>
          )}

          {canViewFunnels && (
            <Box key="funnels-group" sx={{ pl: sidebarOpen ? 2 : 0 }}>
              {sidebarOpen ? (
                <Button
                  color="inherit"
                  onClick={() => setShowFunnels((last) => !last)}
                >
                  Funnel
                  {showFunnels ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </Button>
              ) : (
                <Tooltip title="Funnel" placement="right">
                  <IconButton
                    color="inherit"
                    onClick={() => setShowFunnels((last) => !last)}
                  >
                    <FilterAltIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Collapse in={showFunnels}>
                <Menu.ResourceItem key="funnel-stage" name="funnel-stage" />
                <Menu.ResourceItem
                  key="funnel-stage-assignment"
                  name="funnel-stage-assignment"
                />
              </Collapse>
            </Box>
          )}
        </Collapse>
      </Box>
    );
  }

  const logo = sidebarOpen ? (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-end"
      flex={1}
      minWidth={240}
    >
      <QuivrColorLogo />
    </Box>
  ) : null;

  return (
    <Menu
      sx={{
        "&.RaMenu-open": { height: "100%" },
      }}
    >
      {menuItems}
      {logo}
    </Menu>
  );
};
